/**
 * Spec 107 — PagePersonaEvaluationsPage.
 *
 * Lists the selected brand's page persona-fit evaluations (the CONTENT-QUALITY
 * HYPOTHESIS lens) and provides a "Run page persona-fit" trigger with a persona
 * picker + a page-URL input. Each evaluation renders as a PagePersonaFitCard,
 * decomposed into priority / [wellbeing] / trust-provision / anti-signal with the
 * content-quality-hypothesis label, persona-confidence badge, and BOTH caveats.
 *
 * A ThreeLensPanel surfaces the GEO (083), response persona-fit (106), and page
 * persona-fit (107) for the chosen page + persona side by side — the read-only
 * entry point to the 108/109 cross-lens diagnosis.
 *
 * Controls are hidden for non-admins; the API is authoritative (requireRuleAdmin).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { useBrand } from '../lib/brand-context.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../hooks/useToast.js';
import { apiGet } from '../lib/api.js';
import { listPersonas } from '../api/personas.js';
import type { Persona } from '../api/personas.js';
import {
  listPagePersonaEvaluations,
  getPagePersonaEvaluation,
  runPagePersonaEvaluation,
  deletePagePersonaEvaluation,
  getPagePersonaEvaluationMarkdown,
  getThreeLens,
  type PagePersonaEvaluation,
  type ThreeLensComposition,
} from '../api/pagePersonaEvaluations.js';
import { PagePersonaFitCard } from '../components/PagePersonaFitCard.js';
import { BulkPagePersonaFitModal } from '../components/BulkPagePersonaFitModal.js';
import { ThreeLensPanel } from '../components/ThreeLensPanel.js';
import { EmptyState } from '../components/EmptyState.js';
import { Select } from '../components/Select.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { RowList } from '../components/ui/Row.js';

export function PagePersonaEvaluationsPage() {
  const { selectedBrand } = useBrand();
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const brandId = selectedBrand?.id ?? null;

  const canRun = useMemo(
    () => hasRole('rule_admin') || hasRole('orbit:admin'),
    [hasRole],
  );

  const [personas, setPersonas] = useState<Persona[]>([]);
  // Spec 219 — source domains for the bulk modal. Selection is by DOMAIN
  // because nothing in the data joins a competitor NAME to one, and the same
  // organisation can appear under both (sst.dk / Sundhedsstyrelsen).
  //
  // NOT filtered by ?type=competitor. That was the first attempt and it
  // returned an EMPTY LIST on real data: domain_type is brand-relative and
  // requires the domain to belong to a registered competitor BRAND, which
  // Modrehjaelpen, sst.dk and the rest are not — they are simply domains that
  // get cited. Measured on staging: 100 domains, every domain_type null, zero
  // matching the filter, so the modal offered nothing to select and the run
  // could not be started at all. The domains that HAVE pages are the ones
  // worth offering; the brand's own domain is dropped below since evaluating
  // your own pages is a different question.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [competitorDomains, setCompetitorDomains] = useState<string[]>([]);
  const [evaluations, setEvaluations] = useState<PagePersonaEvaluation[]>([]);
  const [threeLens, setThreeLens] = useState<ThreeLensComposition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPersona, setSelectedPersona] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const [p, e] = await Promise.all([
        listPersonas(brandId).catch(() => []),
        listPagePersonaEvaluations(brandId).catch(() => []),
      ]);
      setPersonas(p);
      // Hydrate dimension results + caveats for the decomposition.
      const hydrated = await Promise.all(
        e.map((row) =>
          getPagePersonaEvaluation(brandId, row.id).catch(() => row),
        ),
      );
      setEvaluations(hydrated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onRun = useCallback(async () => {
    if (!brandId || !selectedPersona || !pageUrl.trim()) return;
    setRunning(true);
    try {
      await runPagePersonaEvaluation(brandId, {
        page_url: pageUrl.trim(),
        persona_id: selectedPersona,
      });
      addToast('Page persona-fit evaluation enqueued', 'success');
      await refresh();
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'Failed to enqueue evaluation',
        'error',
      );
    } finally {
      setRunning(false);
    }
  }, [brandId, selectedPersona, pageUrl, addToast, refresh]);

  const onLoadThreeLens = useCallback(async () => {
    if (!brandId || !selectedPersona || !pageUrl.trim()) return;
    try {
      const comp = await getThreeLens(brandId, pageUrl.trim(), selectedPersona);
      setThreeLens(comp);
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'Failed to load three-lens panel',
        'error',
      );
    }
  }, [brandId, selectedPersona, pageUrl, addToast]);

  // Spec 115 — delete an evaluation (optimistic removal; re-sync on failure).
  const onDeleteEval = useCallback(
    async (evalId: string) => {
      if (!brandId) return;
      setEvaluations((prev) => prev.filter((e) => e.id !== evalId));
      try {
        await deletePagePersonaEvaluation(brandId, evalId);
        addToast('Evaluation deleted', 'success');
      } catch (err) {
        addToast(
          err instanceof Error ? err.message : 'Failed to delete evaluation',
          'error',
        );
        await refresh();
      }
    },
    [brandId, addToast, refresh],
  );

  // Spec 215 — download one evaluation as markdown. Blob logic mirrors
  // PageEvaluationsPage.handleExport and BrandReportButton.handleDownload; the
  // server owns both the content and the filename, so nothing about the naming
  // is re-derived here.
  async function onExportEval(evalId: string) {
    if (!brandId) return;
    try {
      const { markdown, filename } = await getPagePersonaEvaluationMarkdown(brandId, evalId);
      const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not download this evaluation.');
    }
  }

  useEffect(() => {
    if (!brandId || !selectedBrand?.tenant_id) return;
    let cancelled = false;
    void (async () => {
      try {
        // Derive the domain list from the SAME data the URL selection uses:
        // per-URL mention counts. The domains endpoint caps at 100 and does not
        // order by relevance — measured on Sundhed.dk it returns 100 of 417,
        // starting engodstart.dk / veddammen.dk / apoteket.dk, so sst.dk,
        // sexogsamfund.dk and cancer.dk were not offered at all and a bulk run
        // could only be started for whichever targets happened to land in the
        // first page. Ranking by citation volume puts the domains that actually
        // have pages first, and uses one source of truth for both lists.
        const r = await apiGet<{
          data: Array<{ url: string; domain_name: string | null; mention_count: number }>;
        }>(
          `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${brandId}/sources/urls?limit=500`,
        );
        const own = (selectedBrand.domain ?? '').replace(/^www\./, '').toLowerCase();
        const totals = new Map<string, number>();
        for (const row of r.data ?? []) {
          const d = (row.domain_name ?? '').trim();
          if (!d || d.replace(/^www\./, '').toLowerCase() === own) continue;
          totals.set(d, (totals.get(d) ?? 0) + (row.mention_count ?? 0));
        }
        const ranked = [...totals.entries()]
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .map(([d]) => d);
        if (!cancelled) setCompetitorDomains(ranked);
      } catch {
        if (!cancelled) setCompetitorDomains([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandId, selectedBrand?.tenant_id]);

  if (!brandId) {
    return (
      <div className="page-persona-evaluations-page hub-page--table">
        Select a brand to view page persona-fit evaluations.
      </div>
    );
  }

  return (
    <div
      className="page-persona-evaluations-page hub-page--table"
      data-testid="page-persona-evaluations-page"
    >
      <Breadcrumbs
        items={[
          { label: 'Personas', to: '/personas' },
          { label: 'Page persona-fit' },
        ]}
      />
      <header>
        <h1 className="hub-page__title flex items-center gap-2">
          <Icon name="bx-group" className="text-violet-600" />
          Page persona-fit
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          How well a page is <strong>written</strong> for this persona — judged on
          what the page itself provides, not what it cites. This is a
          content-quality hypothesis: it sits alongside the GEO-rules score and the
          AI-answer persona-fit, never merged into one number, and it inherits the
          persona&rsquo;s confidence grade.
        </p>
      </header>

      {canRun && (
        <div
          className="run-page-persona-eval-bar hub-plate flex flex-wrap items-center gap-3"
        >
          <Select
            data-testid="ppe-persona-picker"
            className="min-w-56"
            placeholder="Pick a persona…"
            value={selectedPersona}
            onChange={(v) => setSelectedPersona(v)}
            options={personas.map((p) => ({
              value: p.persona_id,
              label: `${p.name} (${p.persona_type})`,
            }))}
          />
          <input
            data-testid="ppe-url-input"
            type="text"
            placeholder="https://example.com/key-page"
            value={pageUrl}
            onChange={(e) => setPageUrl(e.target.value)}
            className="min-w-[280px] flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm bg-white focus:border-violet-500 focus:ring-1 focus:ring-violet-500 focus:outline-none"
          />
          <Button
            variant="primary"
            data-testid="run-page-persona-eval"
            disabled={running || !selectedPersona || !pageUrl.trim()}
            onClick={() => void onRun()}
            title={
              selectedPersona && pageUrl.trim()
                ? 'Evaluate how well this page is written for the selected persona (queues a page persona-fit job).'
                : 'Pick a persona and enter a page URL first.'
            }
            aria-label="Run page persona-fit evaluation"
          >
            {running ? 'Running…' : 'Run page persona-fit'}
          </Button>
          <Button
            variant="ghost"
            data-testid="load-three-lens"
            disabled={!selectedPersona || !pageUrl.trim()}
            onClick={() => void onLoadThreeLens()}
            title={
              selectedPersona && pageUrl.trim()
                ? 'Show the GEO-rules, AI-answer, and page lenses for this page + persona side by side.'
                : 'Pick a persona and enter a page URL first.'
            }
            aria-label="Compare all three lenses side by side"
          >
            Compare all three views
          </Button>
          {/* Spec 219 — bulk across competitor pages. Opens a dialog rather
              than running anything: it can queue sixty live judge calls, so
              the preview and confirmation are the point, not a formality.

              Spec 230: shares the `<Button variant="ghost">` primitive with
              "Compare all three views" above, so the two can no longer drift
              the way the hand-rolled classes once did (see the incident this
              comment used to describe — an invented class name that
              typechecked, built, and passed its testid while showing no
              visible button at all). One shared recipe closes that class of
              bug rather than re-documenting the risk at each call site. */}
          <Button
            variant="ghost"
            data-testid="bulk-ppf-open"
            onClick={() => setBulkOpen(true)}
            title="Evaluate competitors' most-cited pages against several personas at once."
            aria-label="Open the bulk page persona-fit dialog"
          >
            <Icon name="bx-layer" />
            Bulk across competitors
          </Button>
        </div>
      )}
      {canRun && (
        <p className="text-xs text-slate-500">
          <strong>Run page persona-fit</strong> evaluates the page for the chosen
          persona; <strong>Compare all three views</strong> shows the GEO-rules,
          AI-answer, and page lenses side by side. Pick a persona and enter a page
          URL to enable both.
        </p>
      )}

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}
      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {threeLens && <ThreeLensPanel composition={threeLens} />}

      <div className="page-persona-evals-list mt-3">
        {evaluations.length === 0 && !loading && (
          <EmptyState
            icon="bx-group"
            variant="no-results"
            title="No page persona-fit evaluations yet"
            description="Pick a persona and a page URL above, then run an evaluation."
          />
        )}
        {bulkOpen && brandId && (
          <BulkPagePersonaFitModal
            brandId={brandId}
            personas={personas}
            domains={competitorDomains}
            onClose={() => setBulkOpen(false)}
          />
        )}
        {evaluations.length > 0 && (
          <RowList>
            {evaluations.map((e) => (
              <PagePersonaFitCard
                key={e.id}
                evaluation={e}
                canDelete={canRun}
                onDelete={onDeleteEval}
                onExport={onExportEval}
              />
            ))}
          </RowList>
        )}
      </div>
    </div>
  );
}
